import React, { useState, useEffect } from "react";

/** Animates the card reveal on top the pile (i.e. the result of a call)
 *
 * @param revealedCards
 * @param parseCard
 * @param state
 * @returns {JSX.Element|null}
 * @constructor
 */
export function CardRevealOverlay({revealedCards, parseCard, state}) {
	if (!revealedCards) return null;
	return (<div className="fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[60]">
		<div
			className="text-center bg-opacity-80 backdrop-blur-md rounded-2xl p-6 border-2 border-white border-opacity-20 shadow-2xl">
			{/* Dramatic result text */}
			<div
				className="text-5xl font-bold mb-4 satisfy-regular whitespace-nowrap"
				style={{
					animation: 'dramaticZoom 0.8s ease-out',
					color: revealedCards.wasLying ? '#ef4444' : '#9BE9B7',
					filter: revealedCards.wasLying ? 'drop-shadow(0 0 30px rgba(239, 68, 68, 1.0))' : 'drop-shadow(0 0 50px rgba(34, 197, 94, 0.8))'
				}}
			>
				{revealedCards.wasLying ? '🎭 Busted!' : "No lies detected!"}
			</div>

			{/* Player info */}
			<div className="text-2xl mb-6 text-white drop-shadow-lg whitespace-nowrap">
				{revealedCards.accused === state.your_info.id ? "You" : `${revealedCards.accused_name}`} played:
			</div>

			{/* Cards - only show if they were lying */}
			{(<div className="flex justify-center gap-6 perspective-1000">
				{revealedCards.cards.map((card, i) => {
					const {rank, suit, isRed} = parseCard(card);
					return (<div
						key={i}
						className="relative"
						style={{
							animation: `cardFlipReveal 1s ease-out ${i * 0.15}s both`
						}}
					>
						{/* Card back (shows first during flip) */}
						<div
							className="absolute inset-0 w-20 h-28 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg border-4 border-white shadow-2xl"
							style={{
								backfaceVisibility: 'hidden', animation: `cardFlipOut 1s ease-out ${i * 0.15}s both`
							}}
						>
							<div className="absolute inset-2 border-2 border-blue-400 rounded"></div>
						</div>

						{/* Card front (reveals after flip) */}
						<div
							className={`w-20 h-28 bg-white rounded-lg border-4 border-gray-400 shadow-2xl relative`}
							style={{
								backfaceVisibility: 'hidden', animation: `cardFlipIn 1s ease-out ${i * 0.15}s both`
							}}
						>
							{/* Top-left corner */}
							<div
								className={`absolute top-1 left-2 text-sm leading-none ${isRed ? 'text-red-600' : 'text-gray-900'}`}>
								<div className="font-bold text-lg">{rank}{suit}</div>
							</div>

							{/* Center suit */}
							<div
								className={`absolute inset-0 flex items-center justify-center text-5xl ${isRed ? 'text-red-600' : 'text-gray-900'}`}>
								{suit}
							</div>

							{/* Bottom-right corner */}
							<div
								className={`absolute bottom-1 right-2 text-sm leading-none transform rotate-180 ${isRed ? 'text-red-600' : 'text-gray-900'}`}>
								<div className="font-bold text-lg">{rank}{suit}</div>
							</div>
						</div>
					</div>);
				})}
			</div>)}

			<div className="text-2xl mt-6 mb-2 text-white drop-shadow-lg whitespace-nowrap">
				{revealedCards.wasLying && revealedCards.accused === state.your_info.id ? "You pick" : revealedCards.wasLying ? `${revealedCards.accused_name} picks` : revealedCards.caller === state.your_info.id ? 'You pick' : `${revealedCards.caller_name} picks`} up the
					pile.
			</div>

		</div>
	</div>)
}

function WinnerCards({actualCards, parseCard}) {
	return (
		<div className="flex justify-center gap-6 perspective-1000 mb-4">
			{actualCards.map((card, i) => {
				const {rank, suit, isRed} = parseCard(card);
				return (
					<div key={i} className="relative"
						style={{animation: `cardFlipReveal 1s ease-out ${i * 0.15}s both`}}>
						<div
							className="absolute inset-0 w-20 h-28 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg border-4 border-white shadow-2xl"
							style={{backfaceVisibility: 'hidden', animation: `cardFlipOut 1s ease-out ${i * 0.15}s both`}}>
							<div className="absolute inset-2 border-2 border-blue-400 rounded"></div>
						</div>
						<div className="w-20 h-28 bg-white rounded-lg border-4 border-gray-400 shadow-2xl relative"
							style={{backfaceVisibility: 'hidden', animation: `cardFlipIn 1s ease-out ${i * 0.15}s both`}}>
							<div className={`absolute top-1 left-2 text-sm leading-none ${isRed ? 'text-red-600' : 'text-gray-900'}`}>
								<div className="font-bold text-lg">{rank}{suit}</div>
							</div>
							<div className={`absolute inset-0 flex items-center justify-center text-5xl ${isRed ? 'text-red-600' : 'text-gray-900'}`}>
								{suit}
							</div>
							<div className={`absolute bottom-1 right-2 text-sm leading-none transform rotate-180 ${isRed ? 'text-red-600' : 'text-gray-900'}`}>
								<div className="font-bold text-lg">{rank}{suit}</div>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
}

export function GameOverOverlay({
	gameOver,
	winner,
	gameOverDetails,
	parseCard,
	state,
	ws,
	setSelectedCards,
	setDeclaredRank,
	setHasActed,
	setPileCards,
	setActionQueue,
	setIsNewRound,
	setDiscards,
	onQuit,
	countdown,
	confirmedCount,
	totalHumans,
	setPlayAnnouncements,
	experimentalMode,
	experimentOver,
	hasClickedNextRound,
	setHasClickedNextRound,
	onFinish,
}) {
	useEffect(() => {
		if (!experimentalMode) return;
		if (countdown === null || countdown > 2 || hasClickedNextRound) return;
		if (experimentOver) return;
		ws.send(JSON.stringify({type: "new_round"}));
		if (totalHumans > 1) {
			ws.send(JSON.stringify({type: "human_message", message: "Player joined", sender_id: state.your_info.id}));
		}
		setSelectedCards([]);
		setDeclaredRank("");
		setHasActed(false);
		setPileCards([]);
		setActionQueue([]);
		setIsNewRound(true);
		setDiscards([]);
		setPlayAnnouncements([]);
		setHasClickedNextRound(true);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [countdown]);

	if (!gameOver) return null;

	const isStalemate = winner === null;
	const isYou = winner === state.your_info.name;
	const hasCards = gameOverDetails?.declaredRank && gameOverDetails?.actualCards?.length > 0;

	return (
		<div className="fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-40">
			<div className="text-center bg-opacity-80 backdrop-blur-md rounded-2xl p-6 border-2 border-white border-opacity-20 shadow-2xl">

				{/* Title */}
				<div
					className="text-5xl font-bold mb-4 satisfy-regular whitespace-nowrap"
					style={{
						animation: 'dramaticZoom 0.8s ease-out',
						color: isStalemate ? '#FCD34D' : '#9BE9B7',
						filter: isStalemate
							? 'drop-shadow(0 0 30px rgba(252, 211, 77, 0.8))'
							: 'drop-shadow(0 0 50px rgba(34, 197, 94, 0.8))'
					}}
				>
					{isStalemate ? '🤝 Stalemate!' : isYou ? '🎉 You win!' : `🎉 ${winner} wins!`}
				</div>

				{/* Case 1: stalemate */}
				{isStalemate && (
					<div className="text-xl mb-6 text-white drop-shadow-lg">
						Everyone is left holding Aces, so there's no winner this round.
					</div>
				)}

				{/* Case 2: truthful last play — show the cards */}
				{!isStalemate && hasCards && (
					<>
						<div className="text-2xl mb-4 text-white drop-shadow-lg whitespace-nowrap">
							{isYou ? "You" : winner} played:
						</div>
						<WinnerCards actualCards={gameOverDetails.actualCards} parseCard={parseCard} />
						<div className="text-xl mb-4 text-white/80 drop-shadow-lg">
							{isYou ? "You are" : "They are"} out of cards!
						</div>
					</>
				)}

				{/* Case 3: next player forgot to call */}
				{!isStalemate && !hasCards && (
					<div className="text-2xl mb-6 text-white drop-shadow-lg">
						{isYou ? "You are" : "They are"} out of cards!
					</div>
				)}

				{/* Show countdown and player count */}
				{!hasClickedNextRound && ((countdown !== null && countdown <= 15) || (confirmedCount > 0)) && (
					<div className="mb-2 text-sm">
						{experimentalMode
						? <div className="text-sm text-gray-200">The next game will automatically start in {countdown}s</div>
						: <div className="text-sm text-red-200">You will be disconnected in {countdown}s</div>
					}
						{confirmedCount > 0 && !experimentalMode && (
							<div className="mb-4 text-grey-200">{confirmedCount}/{totalHumans} players ready</div>
						)}
					</div>
				)}

				<div className={`${experimentalMode ? '' : 'flex'} gap-4 justify-center`}>

					{/* Next round: always shown unless experiment is over */}
					{((!experimentalMode) || (experimentalMode && !experimentOver)) && (
						<div className="flex flex-col items-center gap-2">
							<button
								disabled={hasClickedNextRound}
								onClick={() => {
									ws.send(JSON.stringify({type: "new_round"}));
									if (totalHumans > 1) {
										ws.send(JSON.stringify({type: "human_message", message: "Player joined", sender_id: state.your_info.id}));
									}
									setSelectedCards([]);
									setDeclaredRank("");
									setHasActed(false);
									setPileCards([]);
									setActionQueue([]);
									setIsNewRound(true);
									setDiscards([]);
									setPlayAnnouncements([]);
									setHasClickedNextRound(true);
								}}
								className={`${hasClickedNextRound ? 'bg-gray-500 cursor-not-allowed text-gray-400' : 'bg-green-600 hover:bg-green-700 text-white'} font-bold py-2 px-6 rounded-lg text-lg whitespace-nowrap`}
							>
								Next Round
							</button>
							{hasClickedNextRound && experimentalMode && (
								<div className="text-sm text-gray-300 whitespace-nowrap">
									Waiting for the other participants<span className="dot-bounce"><span>.</span><span>.</span><span>.</span></span>
								</div>
							)}
						</div>
					)}

					{/* Experimental mode: show Finish when experiment is over */}
					{experimentalMode && experimentOver && (
						<button
							onClick={onFinish}
							className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-xl text-lg whitespace-nowrap"
						>
							Finish
						</button>
					)}

					{/* Leave Game: only outside experimental mode */}
					{!experimentalMode && (
						<button
							onClick={onQuit}
							className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-lg transition-colors text-lg whitespace-nowrap"
						>
							Leave Game
						</button>
					)}
				</div>
			</div>
		</div>
	);
}

export function ConnectionDroppedOverlay({connectionDropped, isReconnecting, showReconnected}) {
	// Delay showing the spinner badge so instant reconnects don't cause a flash,
	// but the transparent click-blocker appears immediately (isReconnecting is set
	// at the very start of the reconnect attempt).
	const [showSpinner, setShowSpinner] = useState(false);
	useEffect(() => {
		if (!isReconnecting) { setShowSpinner(false); return; }
		const t = setTimeout(() => setShowSpinner(true), 600);
		return () => clearTimeout(t);
	}, [isReconnecting]);

	if (!connectionDropped && !isReconnecting && !showReconnected) return null;

	if (showReconnected) {
		return (
			<div className="fixed bottom-4 right-4 z-[73] flex items-center gap-2 bg-green-600 bg-opacity-95 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg">
				<svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
				</svg>
				Reconnected
			</div>
		);
	}

	if (isReconnecting) {
		return (
			<>
				{/* Transparent blocker: appears immediately so the user can't play while offline */}
				<div className="fixed inset-0 z-[72]" />
				{showSpinner && (
					<div className="fixed bottom-4 right-4 z-[71] flex items-center gap-2 bg-gray-900 bg-opacity-90 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg">
						<svg className="animate-spin w-4 h-4 text-white flex-shrink-0" fill="none" viewBox="0 0 24 24">
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
							<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
						</svg>
						Reconnecting...
					</div>
				)}
			</>
		);
	}

	return (
		<div className="fixed inset-0 flex items-center justify-center z-[70] bg-black bg-opacity-60 backdrop-blur-sm">
			<div className="text-center bg-white rounded-2xl p-8 shadow-2xl max-w-[90%]">
				<div className="text-5xl mb-4">⚠️</div>
				<div className="text-2xl font-bold text-gray-900 mb-3">Connection lost</div>
				<div className="text-lg text-gray-600 mb-6">We are sorry, your connection to the game server has been lost. Please contact the administrator.</div>
				<button
					onClick={() => { window.location.href = '/'; }}
					className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg text-lg"
				>
					Home
				</button>
			</div>
		</div>
	);
}

export function KickedOutOverlay({ kickedOut, onFinish }) {
	if (!kickedOut) return null;
	return (

		<div className="fixed inset-0 flex items-center justify-center z-[70] bg-black bg-opacity-60 backdrop-blur-sm">
			<div className="text-center bg-white rounded-2xl p-8 shadow-2xl max-w-[90%]">
				<div className="text-5xl mb-4">⚠️</div>
					<div className="text-2xl font-bold text-gray-900 mb-3">Disconnected</div>
					<div className="text-lg text-gray-600 mb-6">You were removed from the game due to inactivity.</div>
					<button
					onClick={onFinish}
					className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-xl text-lg whitespace-nowrap"
				>
					Finish
				</button>
			</div></div>
	);
}

export function GameTerminatedOverlay({ gameTerminated, onFinish }) {
	if (!gameTerminated) return null;
	return (
		<div className="fixed inset-0 flex items-center justify-center z-[70] bg-black bg-opacity-60 backdrop-blur-sm">
			<div className="text-center bg-white rounded-2xl p-8 shadow-2xl max-w-[90%]">
				<div className="text-5xl mb-4">🛑</div>
				<div className="text-2xl font-bold text-gray-900 mb-3">Game ended</div>
				<div className="text-lg text-gray-600 mb-6">
					Another player has left, so this game has ended early.<br/>
					Please click Finish to continue to the final survey.
				</div>
				<button
					onClick={onFinish}
					className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-xl text-lg whitespace-nowrap"
				>
					Finish
				</button>
			</div>
		</div>
	);
}

export function TimeoutWarningOverlay({ timeoutRemaining, pileSize }) {
	const [countdown, setCountdown] = useState(null);
	const [showHint, setShowHint] = useState(false);

	useEffect(() => {
		if (timeoutRemaining === null) {
			setCountdown(null);
			setShowHint(false);
			return;
		}
		const deadline = Date.now() + timeoutRemaining * 1000;
		setCountdown(timeoutRemaining);
		const t = setInterval(() => {
			const remaining = Math.max(0, Math.round((deadline - Date.now()) / 1000));
			setCountdown(remaining);
		}, 1000);
		return () => clearInterval(t);
	}, [timeoutRemaining]);

	if (countdown === null) return null;

	return (
		<>
			{showHint && (
				<div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[70]">
					<div className="bg-white rounded-2xl p-6 max-w-sm mx-4 relative pt-10">
						<button
							onClick={() => setShowHint(false)}
							className="absolute top-1 right-1 text-gray-500 hover:text-gray-700 transition-colors z-10"
						>
							<svg className="w-7 h-7 bg-gray-200 hover:bg-gray-300 rounded-2xl transition-colors m-1 p-1"
								fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
						<h2 className="text-2xl font-bold text-gray-800 mb-3">Are you stuck?</h2>
						<p className="text-gray-600">
							{pileSize > 0
								? "It's your turn. You can either call the previous play or select some cards and play to continue the same rank."
								: "It's your turn to play. Select 1–3 cards from your hand and declare a rank to continue."}
						</p>
						<p className="text-gray-400 text-sm mt-2">Make a move before the timer ends to stay in the game. If you need help,
						consult the rules from the game menu.</p>
					</div>
				</div>
			)}
			<div className="fixed bottom-4 right-4 z-[70] flex flex-col items-end gap-2">
				{!showHint && (
					<button
						onClick={() => setShowHint(true)}
						className="bg-green-600 hover:bg-green-500 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg transition-all"
					>
						Need help?
					</button>
				)}
				<div className={`flex items-center gap-2 ${countdown > 15 ? 'bg-gray-900 bg-opacity-90' : 'bg-red-500 bg-opacity-100'} text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg`}>
					<svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
						<circle cx="12" cy="12" r="9" />
						<path strokeLinecap="round" d="M12 7v5l3 3" />
					</svg>
					{countdown}s remaining
				</div>
			</div>
		</>
	);
}

export function GameStartOverlay({showLetsGo}) {
	if (!showLetsGo) return null
	return (
		(<div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
			<div
				className="animate-fadeIn text-center bg-opacity-80 backdrop-blur-sm rounded-2xl
					p-6 border-2 border-white border-opacity-20 shadow-2xl max-w-[75%]"
				style={{
					animation: 'fadeInOut 2.5s ease-in-out',
				}}
			>
				<div className="text-white text-6xl font-bold satisfy-regular "
					style={{
						filter: 'drop-shadow(0 0 30px rgba(256, 256, 256, 0.8))'
					}}
				>
					Let's Gooo!
				</div>
			</div>
		</div>)
	)
}
